import { healPhoneNumberValue } from 'common/src/transformers/healPhoneNumberValue';
import { healZipPostalCodeValue } from 'common/src/transformers/healZipPostalCodeValue';
import { BillProcessingStatus } from 'common/src/types/enums/BillProcessingStatus';
import { ProductFeatures } from 'common/src/types/enums/ProductFeatures.enum';
import { Products } from 'common/src/types/enums/Products.enum';
import { SectorType } from 'common/src/types/enums/SectorType.enum';
import { IOrganization } from 'common/src/types/interfaces';
import { MINIMUM_PASSWORD_EXPIRATION_DAYS } from 'common/src/validators/passwordValidation';
import {
  invalidOrgIdPrefixMsg,
  validateOrgIdPrefix,
} from 'common/src/validators/validateOrganizationId';

import {
  autoCompleteType,
  datePickerType,
  noFieldType,
  numberType,
  selectInputType,
  textInputType,
} from 'portal/src/components/common/forms/FormFieldTypes';
import getInternalNotesField from 'portal/src/components/common/forms/internalNotesField';

export type OrganizationFormFields = ReturnType<typeof defaultValues>;

export const defaultValues = (entity: Partial<IOrganization> = {}) => {
  const fiscalYearStartMonth = new Date();
  fiscalYearStartMonth.setMonth(entity.fiscalYearStartMonth ?? 6);

  return {
    id: entity.id ?? '',
    idPrefix: '', // Only used in organization creation DTO
    billProcessingStatus:
      entity.billProcessingStatus ?? BillProcessingStatus.Provisional,
    esContactAddress1: entity.esContactAddress1 ?? '',
    esContactAddress2: entity.esContactAddress2 ?? '',
    esContactCity: entity.esContactCity ?? '',
    esContactEmail: entity.esContactEmail ?? '',
    esContactFirstName: entity.esContactFirstName ?? '',
    esContactJobTitle: entity.esContactJobTitle ?? '',
    esContactLastName: entity.esContactLastName ?? '',
    esContactPhone: healPhoneNumberValue(entity.esContactPhone) ?? '',
    postalCode: healZipPostalCodeValue(entity.esContactPostalCode, null) ?? '',
    state: entity.esContactState ?? '',
    externalAccountIds: entity.externalAccountIds ?? [],
    fiscalYearStartMonth,
    luzmoCollectionId: entity.luzmoCollectionId ?? '',
    name: entity.name ?? '',
    internalNotes: entity.internalNotes ?? '',
    notes: entity.notes ?? '',
    passwordExpirationDays: entity.passwordExpirationDays ?? null,
    passwordRuleOverrides: entity.passwordRuleOverrides ?? [],
    productFeatures: entity.productFeatures ?? [],
    products: entity.products ?? [],
    sector: entity.sector ?? '',
    ssoOrganizationId: entity.ssoOrganizationId ?? '',
  };
};

export const getOrganizationFields = (
  updateMode: boolean,
  isSysAdmin: boolean,
  isClientSysAdmin: boolean,
): (
  | autoCompleteType
  | datePickerType
  | noFieldType
  | numberType
  | selectInputType
  | textInputType
)[] => [
  {
    name: 'name',
    label: 'Organization Name',
    placeholder: 'Organization Name',
    required: true,
    type: 'textInput',
    disabled: !isClientSysAdmin,
  },
  {
    name: 'id',
    label: 'Organization ID',
    placeholder: 'Organization ID',
    required: false,
    type: 'textInput',
    disabled: true,
    hidden: !updateMode || !isClientSysAdmin,
  },
  {
    name: 'idPrefix',
    label: 'Organization ID Prefix',
    placeholder: 'Organization ID Prefix',
    required: !updateMode,
    type: 'textInput',
    hidden: updateMode,
    validate: (str: string) =>
      validateOrgIdPrefix(str) ||
      invalidOrgIdPrefixMsg.charAt(0).toUpperCase() +
        invalidOrgIdPrefixMsg.slice(1),
  },
  {
    placeholder: 'Fiscal Year Start Month',
    label: 'Fiscal Year Start Month',
    name: 'fiscalYearStartMonth',
    type: 'datePicker',
    required: true,
    other: {
      views: ['month'],
    },
  },
  {
    name: 'sector',
    label: 'Sector',
    placeholder: 'Sector',
    required: true,
    type: 'selectInput',
    values: Object.values(SectorType).map((item) => ({
      label: item,
      value: item,
    })),
    disabled: !isClientSysAdmin,
  },
  {
    label: 'Bill Processing Status',
    name: 'billProcessingStatus',
    placeholder: 'Bill Processing Status',
    type: 'selectInput',
    required: true,
    values: Object.values(BillProcessingStatus).map((item) => ({
      label: item,
      value: item,
    })),
    hidden: !isSysAdmin,
  },
  {
    type: 'noField',
    name: 'noField',
  },
  {
    name: 'products',
    label: 'Products',
    placeholder: 'Products',
    multiple: true,
    type: 'selectInput',
    required: false,
    values: Object.values(Products).map((item) => ({
      label: item,
      value: item,
    })),
    disabled: !isClientSysAdmin,
  },
  {
    name: 'productFeatures',
    label: 'Additional Features',
    placeholder: 'Additional Features',
    multiple: true,
    type: 'selectInput',
    required: false,
    values: Object.values(ProductFeatures).map((item) => ({
      label: item,
      value: item,
    })),
    disabled: !isClientSysAdmin,
  },
  {
    clearOnBlur: true,
    freeSolo: true,
    label: 'External Account IDs',
    multiple: true,
    name: 'externalAccountIds',
    placeholder: 'External Account IDs',
    type: 'autocomplete',
    required: false,
    options: [],
    hidden: !isClientSysAdmin,
  },
  {
    name: 'luzmoCollectionId',
    label: 'Luzmo Collection ID',
    placeholder: 'Luzmo Collection ID',
    type: 'textInput',
    required: false,
    hidden: !isClientSysAdmin,
  },
  {
    name: 'ssoOrganizationId',
    label: 'SSO Organization ID',
    placeholder: 'SSO Organization ID',
    type: 'textInput',
    required: false,
    hidden: !isSysAdmin,
  },
  {
    type: 'number',
    name: 'passwordExpirationDays',
    label: 'Password Expiration Days',
    placeholder: 'Password Expiration Days',
    required: false,
    hidden: !isClientSysAdmin,
    validate: (value) => {
      return (
        value == null ||
        value >= MINIMUM_PASSWORD_EXPIRATION_DAYS ||
        `Value must be greater than or equal to ${MINIMUM_PASSWORD_EXPIRATION_DAYS}`
      );
    },
  },
  {
    label: 'Notes',
    name: 'notes',
    multiline: true,
    placeholder: 'Notes',
    type: 'textInput',
    required: false,
    disabled: !isClientSysAdmin,
  },
  getInternalNotesField(isSysAdmin),
];
